import { getDB, type OutboxOp, type SyncTable } from '../local/db'
import { settleRow } from '../local/cache'
import type { Failure } from './errors'

/**
 * The queue of writes waiting to reach the server, and the rules for folding
 * them together.
 *
 * Compaction matters more than it looks. A ticket opened offline, added to
 * five times and then settled is seven writes; sent as seven they take seven
 * round trips and each one has to be a transition the server's orders_guard
 * trigger recognises. Folded properly they are a single INSERT, and the
 * trigger never runs at all because there is nothing to update.
 *
 * But folding the wrong pair produces a write the trigger refuses, and a
 * refused write is a quarantined sale. So the rules below are narrow on
 * purpose: fold only what is provably safe, and leave the rest alone.
 */

export interface EnqueueRequest {
  tenantId: string
  table: SyncTable
  rowId: string
  type: 'insert' | 'update'
  payload: Record<string, unknown>
}

/**
 * Which of the server's four permitted order transitions a patch represents.
 *
 * Mirrors orders_guard in supabase/rls.sql. Two patches may only be folded
 * together if they are the same kind, merging a round into a settle would
 * produce a single update that changes both `lines` and `status`, which is not
 * one of the four and would be refused.
 */
type Transition = 'round' | 'move' | 'settle' | 'void'

function transitionOf(payload: Record<string, unknown>): Transition {
  if (payload.status === 'void') return 'void'
  if (payload.status === 'completed') return 'settle'
  if ('table_name' in payload && !('lines' in payload)) return 'move'
  return 'round'
}

/**
 * Drops undefined values.
 *
 * Postgres has no undefined; PostgREST either rejects the key or, worse,
 * silently omits it. This is the same class of bug that once made a waiter's
 * button do nothing at all, the write was rejected for carrying an undefined
 * note and the rejection reached only the console. Cut at the source.
 */
export function clean<T extends Record<string, unknown>>(o: T): T {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(o)) {
    if (v !== undefined) out[k] = v
  }
  return out as T
}

/**
 * Adds a write to the queue, folding it into an existing one where that is
 * safe.
 *
 * Returns the seq of the entry the write ended up in, the caller records it
 * against the cached row so the row knows it is still pending.
 */
export async function enqueue(req: EnqueueRequest): Promise<number> {
  const db = await getDB()
  const tx = db.transaction('outbox', 'readwrite')
  const store = tx.objectStore('outbox')

  const payload = clean(req.payload)

  // Everything still queued for this row, oldest first. Anything already
  // inflight or failed is untouchable: inflight may be on the wire, failed is
  // waiting for a human.
  const existing = (await store.index('by_row').getAll([req.table, req.rowId]))
    .filter((op) => op.status === 'pending')
    .sort((a, b) => a.seq - b.seq)

  const insertOp = existing.find((op) => op.type === 'insert')

  // Case 1, the row has never reached the server.
  //
  // Every later change folds into the pending INSERT, whatever kind it is.
  // This is safe precisely because the row does not exist yet: there is no
  // previous state for the trigger to judge a transition against, and an
  // INSERT of the finished article is always valid. It is also the only way
  // to be correct, a PATCH against a row the server has never seen is a 404.
  if (insertOp) {
    insertOp.payload = { ...insertOp.payload, ...payload }
    await store.put(insertOp)
    await tx.done
    return insertOp.seq
  }

  // Case 2, fold into the previous patch if it is the same kind of change.
  //
  // Same transition means the pair collapses into one update that the trigger
  // still recognises: two rounds become one round carrying the later totals.
  const last = existing.at(-1)
  if (
    last &&
    req.type === 'update' &&
    last.type === 'update' &&
    transitionOf(last.payload) === transitionOf(payload)
  ) {
    last.payload = { ...last.payload, ...payload }
    await store.put(last)
    await tx.done
    return last.seq
  }

  // Case 3, a new kind of change. Its own entry, behind whatever is queued.
  const op: Omit<OutboxOp, 'seq'> = {
    tenantId: req.tenantId,
    table: req.table,
    rowId: req.rowId,
    type: req.type,
    payload,
    attempts: 0,
    createdAt: Date.now(),
    nextAttemptAt: 0,
    status: 'pending',
  }
  const seq = (await store.add(op as OutboxOp)) as number
  await tx.done
  return seq
}

/** The next op due to be sent, or undefined if the queue is idle. */
export async function nextReadyOp(now = Date.now()): Promise<OutboxOp | undefined> {
  const db = await getDB()
  const ready = await db.getAllFromIndex(
    'outbox',
    'by_status_time',
    IDBKeyRange.bound(['pending', 0], ['pending', now]),
  )
  // The index orders by nextAttemptAt; ties break on seq so a burst enqueued
  // in the same millisecond still leaves in the order it was written.
  return ready.sort((a, b) => a.nextAttemptAt - b.nextAttemptAt || a.seq - b.seq)[0]
}

export async function markInflight(seq: number): Promise<void> {
  const db = await getDB()
  const op = await db.get('outbox', seq)
  if (op) await db.put('outbox', { ...op, status: 'inflight' })
}

export async function removeOp(seq: number): Promise<void> {
  const db = await getDB()
  await db.delete('outbox', seq)
}

/**
 * Puts an op back in the queue after a failure that may pass.
 *
 * There is no attempt limit. A restaurant runs for hours and the wifi in a
 * dormitory hall can be gone for most of one; "give up after fifty tries"
 * throws away a sale that would have gone through at try fifty-one. The
 * backoff caps at a minute and the badge shows the backlog.
 */
const BACKOFF_MS = [1_000, 2_000, 5_000, 15_000, 30_000, 60_000]

export async function scheduleRetry(op: OutboxOp, failure: Failure): Promise<void> {
  const db = await getDB()
  const attempts = op.attempts + 1
  const base = BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)]
  // Jitter so a hall full of tills coming back online together does not
  // arrive as one thundering herd.
  const wait = base + Math.random() * base * 0.3
  await db.put('outbox', {
    ...op,
    attempts,
    status: 'pending',
    nextAttemptAt: Date.now() + wait,
    lastError: failure,
  })
}

/**
 * Quarantines an op the server will never accept.
 *
 * Kept rather than deleted. The sale is real, somebody took money for it,
 * so the decision to discard it belongs to a person, not to this function.
 */
export async function markFailed(op: OutboxOp, failure: Failure): Promise<void> {
  const db = await getDB()
  await db.put('outbox', { ...op, status: 'failed', lastError: failure })
}

/**
 * Rows with writes still to go. The pending flag comes from this.
 *
 * With `tenantId`: only that institution's, and not the rows the server has
 * refused (their later writes wait behind the refusal and would otherwise
 * count as "kaydediliyor" for ever; they are shown as stuck instead).
 */
export async function pendingRowIds(table: SyncTable, tenantId?: string): Promise<Set<string>> {
  const db = await getDB()
  const all = await db.getAll('outbox')
  const refused = new Set(all.filter((o) => o.status === 'failed').map((o) => `${o.table}:${o.rowId}`))
  return new Set(
    all
      .filter((o) => o.table === table && o.status !== 'failed')
      .filter((o) => tenantId === undefined || (o.tenantId === tenantId && !refused.has(`${o.table}:${o.rowId}`)))
      .map((o) => o.rowId),
  )
}

/**
 * Bir satırın takılı kalmış kayıtları (kullanıcı, 11 Eki 2026: rozet hep
 * sarıydı, çıkışta "1 sipariş kaydedilemedi" diyordu). Sunucunun reddettiği
 * satırlar ve arkalarında bekleyenler; ya da internet varken defalarca
 * gönderilemeyen satırlar. Ekranda listelenip tekrar denenebiliyor ya da
 * siliniyor.
 */
export interface StuckRow {
  table: SyncTable
  rowId: string
  ops: OutboxOp[]
  /** Masa adı ya da kayıttan okunabilen bir ad (yoksa boş). */
  label: string
  /** Sunucu reddetti (yoksa defalarca denenip gönderilemedi). */
  refused: boolean
  reason: string
  since: number
}

/** Bu kadar denemeden sonra, internet varken bile gitmeyen kayıt takılı sayılır. */
const STUCK_ATTEMPTS = 6

export async function stuckRows(tenantId: string): Promise<StuckRow[]> {
  const db = await getDB()
  const all = (await db.getAll('outbox')).filter((o) => o.tenantId === tenantId)
  const byRow = new Map<string, OutboxOp[]>()
  for (const o of all) {
    const k = `${o.table}:${o.rowId}`
    byRow.set(k, [...(byRow.get(k) ?? []), o])
  }
  const out: StuckRow[] = []
  for (const ops of byRow.values()) {
    ops.sort((a, b) => a.seq - b.seq)
    const failed = ops.find((o) => o.status === 'failed')
    const looping = ops.find((o) => o.status !== 'failed' && o.attempts >= STUCK_ATTEMPTS && o.lastError)
    const bad = failed ?? looping
    if (!bad) continue
    const cached = await db.get('rows', [bad.table, bad.rowId])
    const fromOps = ops.map((o) => o.payload.table_name).find((v) => typeof v === 'string' && v)
    const fromCache = (cached?.local?.table_name ?? cached?.server?.table_name) as unknown
    out.push({
      table: bad.table,
      rowId: bad.rowId,
      ops,
      label: String(fromOps ?? (typeof fromCache === 'string' ? fromCache : '') ?? ''),
      refused: !!failed,
      reason: bad.lastError?.message ?? 'Bu kayıt sunucuya gönderilemedi.',
      since: ops[0].createdAt,
    })
  }
  return out.sort((a, b) => a.since - b.since)
}

/** Bir satırın kuyruktaki bütün kayıtlarını siler. Yalnız ekrandan, onayla. */
export async function discardRow(table: SyncTable, rowId: string): Promise<void> {
  const db = await getDB()
  const all = await db.getAll('outbox')
  for (const o of all) if (o.table === table && o.rowId === rowId) await db.delete('outbox', o.seq)
  await settleRow(table, rowId, false)
}

/** Bir satırın reddedilen ve takılan kayıtlarını baştan sıraya koyar. */
export async function retryRow(table: SyncTable, rowId: string): Promise<void> {
  const db = await getDB()
  const all = await db.getAll('outbox')
  for (const o of all) {
    if (o.table !== table || o.rowId !== rowId) continue
    await db.put('outbox', { ...o, status: 'pending', attempts: 0, nextAttemptAt: 0, lastError: undefined })
  }
}

/** Rows the server refused. These show in red and block only themselves. */
export async function failedRowIds(table: SyncTable): Promise<Set<string>> {
  const db = await getDB()
  const all = await db.getAll('outbox')
  return new Set(
    all.filter((o) => o.table === table && o.status === 'failed').map((o) => o.rowId),
  )
}

/** Every quarantined op, for the "could not be sent" screen. */
export async function failedOps(): Promise<OutboxOp[]> {
  const db = await getDB()
  const all = await db.getAll('outbox')
  return all.filter((o) => o.status === 'failed').sort((a, b) => a.seq - b.seq)
}

/**
 * Puts a quarantined op back in the queue.
 *
 * Attempts reset to zero: whatever was wrong is presumed fixed, a cashier
 * signed in again, a role was granted, so it deserves a clean start rather
 * than a minute of backoff inherited from the failure.
 */
export async function retryFailed(seq: number): Promise<void> {
  const db = await getDB()
  const op = await db.get('outbox', seq)
  if (!op) return
  await db.put('outbox', {
    ...op,
    status: 'pending',
    attempts: 0,
    nextAttemptAt: 0,
    lastError: undefined,
  })
}

/** Discards a quarantined op for good. Only ever called from the UI. */
export async function discardFailed(seq: number): Promise<void> {
  const db = await getDB()
  const op = await db.get('outbox', seq)
  await removeOp(seq)
  // The change was thrown away, so its overlay goes with it, left behind it
  // would keep showing a write the server never took.
  if (op) await settleRow(op.table, op.rowId, false)
}

/** How many rows are waiting, counted by row rather than by write. */
export async function pendingCount(table: SyncTable): Promise<number> {
  return (await pendingRowIds(table)).size
}
